/* eslint-disable sonarjs/no-duplicate-string */
import React, { useMemo } from 'react';
import { wizardStyles } from '../../../JSstyles/wizardStyles';
import { Grid, List, ListItem, Box } from '@rentacenter/racstrap';
import { ReactComponent as CompletedIcon } from '../../../assets/images/wizard-complete.svg';
import { useTranslation } from 'react-i18next';
import CustomerInfo from '../../CustomerOrder/Shared/CustomerInfo';

export interface LocalSourcingWizardProps {
  customerId?: string;
  LocalSourcingCompleted: boolean;
  AgreementDetailsCompleted: boolean;
  PreliminaryPaymentCompleted: boolean;
  POStatusCompleted: boolean;
  InitialPaymentCompleted: boolean;
  DeliveryMethodCompleted: boolean;
}

type StepStatus = 'complete' | 'inprogress' | 'pending';

interface StepConfig {
  label: string;
  status: StepStatus;
  stepNumber: number;
}

/**
 * Derives step statuses based on completion flags.
 * The first incomplete step is marked 'inprogress', all before it are 'complete',
 * and all after are 'pending'.
 */
function deriveStepStatuses(props: LocalSourcingWizardProps): StepStatus[] {
  const completionFlags = [
    props.LocalSourcingCompleted,
    props.AgreementDetailsCompleted,
    props.PreliminaryPaymentCompleted,
    props.POStatusCompleted,
    props.InitialPaymentCompleted,
    props.DeliveryMethodCompleted,
  ];

  const statuses: StepStatus[] = [];
  let foundInProgress = false;

  for (let i = 0; i < completionFlags.length; i++) {
    if (completionFlags[i]) {
      statuses.push('complete');
    } else if (!foundInProgress) {
      statuses.push('inprogress');
      foundInProgress = true;
    } else {
      statuses.push('pending');
    }
  }

  return statuses;
}

export default function LocalSourcingWizard(props: LocalSourcingWizardProps) {
  const { t } = useTranslation('customer');
  const wizardClassName = wizardStyles();

  const dynamicClasses: Record<
    StepStatus,
    { anchor: string; spanNum: string; spanDetail: string }
  > = {
    complete: {
      anchor: wizardClassName.smartWizardListLinkActive,
      spanNum: `${wizardClassName.borderDefault} ${wizardClassName.done}`,
      spanDetail: wizardClassName.stepDone,
    },
    inprogress: {
      anchor: wizardClassName.smartWizardListLinkInActive,
      spanNum: wizardClassName.borderActive,
      spanDetail: wizardClassName.stepActive,
    },
    pending: {
      anchor: wizardClassName.smartWizardListLinkInActive,
      spanNum: wizardClassName.borderDefault,
      spanDetail: wizardClassName.stepNotStarted,
    },
  };

  const stepLabels = [
    'Local Sourcing',
    'Agreement Details',
    'Preliminary Payment',
    'PO Status',
    'Initial Payment',
    'Delivery Method',
  ];

  const stepStatuses = useMemo(
    () => deriveStepStatuses(props),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.LocalSourcingCompleted,
      props.AgreementDetailsCompleted,
      props.PreliminaryPaymentCompleted,
      props.POStatusCompleted,
      props.InitialPaymentCompleted,
      props.DeliveryMethodCompleted,
    ]
  );

  const steps: StepConfig[] = stepLabels.map((label, index) => ({
    label,
    status: stepStatuses[index],
    stepNumber: index + 1,
  }));

  return (
    <div data-testid="LocalSourcingWizardTest">
      <Grid className={wizardClassName.swThemeDefault}>
        <Grid item className={wizardClassName.swThemeDefaultContainer}>
          <List className={wizardClassName.smartWizard}>
            {steps.map((step) => (
              <ListItem
                key={step.stepNumber}
                className={wizardClassName.smartWizardList}
              >
                <a
                  className={dynamicClasses[step.status].anchor}
                  style={{ cursor: 'not-allowed' }}
                >
                  {step.status === 'complete' ? (
                    <Box
                      component="span"
                      className={`${wizardClassName.done} ${wizardClassName.borderDone}`}
                    >
                      <CompletedIcon className={wizardClassName.paddingTop3} />
                    </Box>
                  ) : (
                    <span className={dynamicClasses[step.status].spanNum}>
                      {step.stepNumber}
                    </span>
                  )}
                  <span className={dynamicClasses[step.status].spanDetail}>
                    {t(step.label)}
                  </span>
                </a>
              </ListItem>
            ))}
          </List>
          <CustomerInfo
            CoCustomerDetails={{ coCustomerId: '', coCustomerName: '' }}
            dereceiver={false}
            customerId={props.customerId}
          />
        </Grid>
      </Grid>
    </div>
  );
}
